import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Head from 'expo-router/head';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { ArticlesSection } from '@/components/riviera/articles-section';
import { BoatsCarousel } from '@/components/riviera/boats-carousel';
import { CategoryNav } from '@/components/riviera/category-nav';
import { EventsCarousel } from '@/components/riviera/events-carousel';
import { ExperiencesCarousel } from '@/components/riviera/experiences-carousel';
import { FeatureStrip } from '@/components/riviera/feature-strip';
import { FiltersBar } from '@/components/riviera/filters-bar';
import { HeroSearch } from '@/components/riviera/hero-search';
import { MainContent } from '@/components/riviera/main-content';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { FiltersProvider } from '@/hooks/use-filters';
import { useTheme } from '@/hooks/use-theme';
import { useLocale } from '@todo/core/i18n/use-locale';
import {
  BOATS_INDEX_SEED_KEY,
  EVENTS_INDEX_SEED_KEY,
  TOURS_INDEX_SEED_KEY,
  articlesIndexSeedKey,
} from '@todo/core/list-queries';
import { SITE_URL, absoluteUrl, hreflangLinks } from '@todo/core/seo';

import styles from './home.module.css';

// Web-only: the mobile/desktop split used to be a JS `isLg` check (see the
// native app/[lang]/index.tsx), which renders the mobile tree on both the
// server and the client's first paint, then swaps to the desktop tree once
// `useWindowDimensions` resolves after mount — a visible flash of an
// unconstrained, full-width layout on every desktop load. Here the same
// single component tree is placed with CSS grid-area (home.module.css),
// letting the browser pick mobile vs desktop synchronously at first paint.
export default function HomeScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();

  const canonical = absoluteUrl(locale);

  return (
    <FiltersProvider>
      <View style={[rnStyles.root, { backgroundColor: theme.background }]}>
        {/* key forces a fresh mount on locale change — see the comment in
            app/[lang]/_layout.tsx for why. */}
        <Head key={locale}>
          <title>{t('seo.homeTitle')}</title>
          <meta name="description" content={t('seo.homeDescription')} />
          <link rel="canonical" href={canonical} />
          {hreflangLinks().map((link) => (
            <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
          ))}
          <meta property="og:type" content="website" />
          <meta property="og:title" content={t('seo.homeTitle')} />
          <meta property="og:description" content={t('seo.homeDescription')} />
          <meta property="og:url" content={canonical} />
          {/* Per-locale 1200x630 share card (public/og-home-*.jpg) — the
              size summary_large_image cards and messenger previews expect. */}
          <meta property="og:image" content={`${SITE_URL}/og-home-${locale}.jpg`} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={t('seo.homeImageAlt')} />
          <meta name="twitter:title" content={t('seo.homeTitle')} />
          <meta name="twitter:description" content={t('seo.homeDescription')} />
          <meta name="twitter:image" content={`${SITE_URL}/og-home-${locale}.jpg`} />
          <meta name="twitter:image:alt" content={t('seo.homeImageAlt')} />
          {/* Baked rows so the client's first render of the events, tours and
              articles carousels matches this static HTML — see
              list-prefetch.ts. */}
          {listSeedScript(EVENTS_INDEX_SEED_KEY)}
          {listSeedScript(TOURS_INDEX_SEED_KEY)}
          {listSeedScript(BOATS_INDEX_SEED_KEY)}
          {listSeedScript(articlesIndexSeedKey(locale))}
        </Head>
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <ScrollView
          style={{ flex: 1 }}>
          <div className={styles.grid}>
            <div className={styles.hero}>
              <HeroSearch />
            </div>
            <div className={styles.filters}>
              <FiltersBar />
            </div>
            <div className={styles.nav}>
              <CategoryNav />
            </div>
            <div className={styles.events}>
              <EventsCarousel />
            </div>
            <div className={styles.experiences}>
              <ExperiencesCarousel />
            </div>
            <div className={styles.boats}>
              <BoatsCarousel />
            </div>
            <div className={styles.articles}>
              <ArticlesSection />
            </div>
            <div className={styles.feature}>
              <FeatureStrip />
            </div>
            <div className={styles.content}>
              <MainContent />
            </div>
          </div>
          <SiteFooter />
        </ScrollView>
      </View>
    </FiltersProvider>
  );
}

const rnStyles = StyleSheet.create({
  root: {
    flex: 1,
  },
});
